import React from 'react';
import ReactDOM from 'react-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import Editor from '@monaco-editor/react';
import { Eye, EyeOff, Copy, Check, Code } from 'lucide-react';
import VariableHighlighter from '../VariableHighlighter';
import { openFolderVariablesFor } from '../../../utils/openFolderVariables';
import { remeasureMonacoFontsWhenReady } from '../../../utils/monacoFonts';
import { isDynamicVariable } from '../../../utils/dynamicVariables';
import VariableEditorPopup, { VAR_TOOLTIP_Z } from '../VariableEditorPopup';
import { LARGE_TEXT_EDITOR_OPTIONS } from '../../../utils/monacoLargeText';
import { bindEditorText } from '../codeEditorText';
import LargeTextNotice from '../LargeTextNotice';

/** The code editor markup: toolbar, Monaco or the preview, the variable tooltip and editor. Plain function called during render, so the element tree is unchanged. */
export function renderCodeEditor({
  monacoRef,
  language,
  monacoTheme,
  value,
  placeholder,
  readOnly,
  showLineNumbers,
  selectedEnvironment,
  globalVariables,
  collectionVariables,
  folderVariables,
  workspaceVariables,
  onVariableUpdate,
  variableContext,
  hideChrome,
  headerStart,
  isPreviewMode,
  setIsPreviewMode,
  copied,
  varTooltip,
  setVarTooltip,
  varTooltipRef,
  varTooltipBoxRef,
  varTooltipPlace,
  editingVariable,
  setEditingVariable,
  setTextLevel,
  incomingChars,
  setIncomingChars,
  largeText,
  largeTextRef,
  textBindingRef,
  onChangeRef,
  valueRef,
  editorRef,
  editorWrapperRef,
  isDark,
  prismStyle,
  monacoThemeRef,
  claimVariableScopes,
  varDecorationsDataRef,
  updateDecorationsRef,
  onCtrlEnterRef,
  variableContextRef,
  selectedEnvironmentRef,
  saveVariableValue,
  handleMonacoBeforeMount,
  scheduleApplyMonacoLayout,
  formatCode,
  formatCodeRef,
  copyToClipboard,
  getLanguageForHighlighter,
  getMonacoLanguage,
  monacoEditorOptions,
}) {
  const noFrame = hideChrome || headerStart != null;

  const toolbarActions = (
    <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2">
      {(language === 'json' || language === 'xml') && !readOnly && (
        <button
          type="button"
          onClick={formatCode}
          className="cursor-pointer rounded bg-transparent px-2 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
          title="Format code"
        >
          Format
        </button>
      )}
      {!largeText && <button
        type="button"
        onClick={() => setIsPreviewMode(!isPreviewMode)}
        className={`cursor-pointer rounded bg-transparent p-1 transition-colors ${
          isPreviewMode
            ? 'bg-blue-50 text-blue-600 dark:bg-zinc-800 dark:text-blue-400'
            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200'
        }`}
        title={isPreviewMode ? 'Edit mode' : 'Preview mode'}
      >
        {isPreviewMode ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>}
      <button
        type="button"
        onClick={copyToClipboard}
        className="cursor-pointer rounded bg-transparent p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
        title="Copy to clipboard"
      >
        {copied ? <Check size={16} className="text-green-500 dark:text-green-400" /> : <Copy size={16} />}
      </button>
    </div>
  );

  return (
    <div
      className={
        noFrame
          ? 'h-full min-h-0 flex flex-col overflow-hidden'
          : 'border border-gray-300 rounded-lg overflow-hidden h-full flex flex-col'
      }
    >
      {!noFrame && (
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900/80">
          <div className="flex items-center space-x-2">
            <Code size={16} className="text-gray-500 dark:text-zinc-500" />
            <span className="text-sm font-medium capitalize text-gray-700 dark:text-zinc-200">{language}</span>
          </div>
          {toolbarActions}
        </div>
      )}
      {headerStart != null && (
        <div className="flex min-w-0 flex-shrink-0 items-center justify-between gap-3 px-5 py-0.5">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{headerStart}</div>
          {toolbarActions}
        </div>
      )}
      {largeText && !isPreviewMode && <LargeTextNotice chars={value?.length || incomingChars} />}
      {/*
        Outer: flex-sized slot only (ResizeObserver target). Inner: absolute fill so Monaco’s
        explicit height cannot inflate this flex item’s min-content and block shrinking (response pane).
      */}
      <div
        ref={editorWrapperRef}
        className="relative min-h-0 flex-1 overflow-hidden"
        style={{ minHeight: 0, backgroundColor: isDark ? '#161618' : '#ffffff' }}
      >
        {!isPreviewMode ? (
          <div
            className="absolute inset-0 min-h-0 min-w-0 overflow-hidden"
            onClick={() => {
              const t = varTooltipRef.current;
              if (!t || isDynamicVariable(t.name)) return;
              // Folder values are edited in the Folder variables modal (D2).
              if (t.type === 'folder') {
                openFolderVariablesFor(t, variableContextRef.current);
                setVarTooltip(null);
                return;
              }
              const defaultScope =
                t.type ||
                (variableContextRef.current?.collectionId ? 'collection' : null) ||
                (selectedEnvironmentRef.current ? 'environment' : null) ||
                (variableContextRef.current?.workspaceId ? 'workspace' : null) ||
                'global';
              setEditingVariable({ name: t.name, value: t.rawValue || '', type: defaultScope, exists: t.found, x: t.x, y: t.y });
              setVarTooltip(null);
            }}
          >
            <Editor
            width="100%"
            height="100%"
            language={getMonacoLanguage()}
            theme={monacoTheme}
            beforeMount={handleMonacoBeforeMount}
            // Only the model's starting text: after mount ./codeEditorText keeps
            // model and props in step (the wrapper's own sync read the whole
            // model twice per keystroke).
            defaultValue={value}
            onMount={(editor, monaco) => {
              editorRef.current = editor;
              monacoRef.current = monaco;
              // A large text updates its `{{var}}` marks once typing pauses, and
              // on scroll (only the visible part is scanned).
              let decorationTimer = null;
              const refreshDecorations = () => {
                if (!largeTextRef.current) {
                  updateDecorationsRef.current?.();
                  return;
                }
                clearTimeout(decorationTimer);
                decorationTimer = setTimeout(() => updateDecorationsRef.current?.(), 150);
              };
              const binding = bindEditorText(editor, monaco, {
                onChange: (next) => onChangeRef.current?.(next),
                onLargeChange: (level, chars) => {
                  largeTextRef.current = level >= 1;
                  // Now, not on the next render: this can run inside a paste
                  // that Monaco applies right after (./codeEditorText).
                  if (level >= 1) editor.updateOptions(LARGE_TEXT_EDITOR_OPTIONS);
                  setTextLevel(level);
                  setIncomingChars(chars || 0);
                },
                onEdit: refreshDecorations,
              });
              textBindingRef.current = binding;
              binding.applyExternal(valueRef.current, readOnly);
              editor.onDidScrollChange((e) => {
                if (largeTextRef.current && e.scrollTopChanged) refreshDecorations();
              });
              editor.onDidDispose(() => {
                clearTimeout(decorationTimer);
                binding.dispose();
                if (textBindingRef.current === binding) textBindingRef.current = null;
              });
              monaco.editor.setTheme(monacoThemeRef.current);
              // JetBrains Mono may still be loading; without this Monaco keeps
              // the fallback font's metrics and selections/caret drift.
              remeasureMonacoFontsWhenReady(monaco);
              scheduleApplyMonacoLayout();

              // FOCUS decides who owns the completion list, not mounting: a
              // request tab mounts a body editor and two script editors, and
              // publishing on mount let the last one to mount answer for all
              // three. If this editor already has the caret when it mounts
              // (restored tab, autofocus), it claims now.
              if (editor.hasTextFocus?.()) claimVariableScopes();
              editor.onDidFocusEditorText(claimVariableScopes);

              // "To Variable" on the body editor — an API key buried in a JSON
              // payload is the case this feature exists for. Added as a Monaco
              // ACTION rather than a custom menu, so the editor's own Cut, Copy
              // and Paste stay exactly where they were.
              editor.addAction({
                id: 'apiboo.toVariable',
                label: 'To Variable',
                contextMenuGroupId: 'navigation',
                contextMenuOrder: 0,
                // Greyed out without a selection, the same as Copy.
                precondition: 'editorHasSelection',
                run: (ed) => {
                  const selection = ed.getSelection();
                  const model = ed.getModel();
                  if (!selection || !model || selection.isEmpty()) return;
                  const selected = model.getValueInRange(selection);
                  if (!selected.trim()) return;
                  // A selection straddling an existing `{{token}}` would corrupt
                  // both halves — same guard the URL field applies.
                  if (/\{\{|\}\}/.test(selected)) return;

                  const pos = ed.getScrolledVisiblePosition(selection.getEndPosition());
                  const box = ed.getDomNode()?.getBoundingClientRect();
                  setEditingVariable({
                    name: '',
                    nameEditable: true,
                    value: selected,
                    exists: false,
                    replaceRange: selection,
                    x: (box?.left ?? 0) + (pos?.left ?? 0),
                    y: (box?.top ?? 0) + (pos?.top ?? 0),
                  });
                },
              });

              // Custom variable tooltip on hover.
              editor.onMouseMove((e) => {
                const pos = e.target?.position;
                if (!pos) { setVarTooltip(null); return; }
                const data = varDecorationsDataRef.current;
                const hit = data.find(
                  (d) =>
                    pos.lineNumber >= d.range.startLineNumber &&
                    pos.lineNumber <= d.range.endLineNumber &&
                    (pos.lineNumber !== d.range.startLineNumber || pos.column >= d.range.startColumn) &&
                    (pos.lineNumber !== d.range.endLineNumber || pos.column <= d.range.endColumn)
                );
                if (hit) {
                  const browserEvt = e.event?.browserEvent;
                  setVarTooltip({ ...hit, x: browserEvt?.clientX ?? 0, y: browserEvt?.clientY ?? 0 });
                } else {
                  setVarTooltip(null);
                }
              });

              editor.onMouseLeave(() => setVarTooltip(null));

              if (!readOnly) {
                editor.addCommand(
                  monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF,
                  () => { formatCodeRef.current?.(); }
                );
              }

              // Ctrl+Enter / Cmd+Enter → trigger send request.
              editor.addCommand(
                monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
                () => { onCtrlEnterRef.current?.(); }
              );

              // Initial decoration pass after mount.
              requestAnimationFrame(() => {
                if (updateDecorationsRef.current) updateDecorationsRef.current();
              });
            }}
            options={monacoEditorOptions}
            loading={
              <div className="absolute inset-0" style={{ backgroundColor: isDark ? '#161618' : '#ffffff' }} />
            }
            />
          </div>
        ) : (
          <div className="absolute inset-0 min-h-0 overflow-auto panel-scroll">
            <div className="p-3">
              <VariableHighlighter
                text={value || placeholder}
                selectedEnvironment={selectedEnvironment}
                globalVariables={globalVariables}
                collectionVariables={collectionVariables}
                folderVariables={folderVariables}
                workspaceVariables={workspaceVariables}
                onVariableUpdate={onVariableUpdate}
                variableContext={variableContext}
              />
            </div>
            <SyntaxHighlighter
              language={getLanguageForHighlighter()}
              style={prismStyle}
              showLineNumbers={showLineNumbers}
              className="syntax-highlighter !m-0 !bg-transparent text-sm"
              customStyle={{
                margin: 0,
                padding: '12px',
                background: 'transparent',
                fontSize: '14px',
                lineHeight: '1.5'
              }}
            >
              {value || placeholder}
            </SyntaxHighlighter>
          </div>
        )}
      </div>

      {/* Monaco variable tooltip — portaled to body to escape overflow:hidden */}
      {varTooltip && !editingVariable && ReactDOM.createPortal(
        <div
          ref={varTooltipBoxRef}
          className="variable-tooltip fixed pointer-events-none"
          // Above the variable-editor stack, which a nested editor grows, and
          // inside the window.
          style={{
            left: varTooltipPlace?.left ?? varTooltip.x + 14,
            top: varTooltipPlace?.top ?? varTooltip.y + 16,
            zIndex: VAR_TOOLTIP_Z,
          }}
        >
          {varTooltip.found ? (
            <>
              <div className="variable-tooltip-top">
                <span className="variable-tooltip-name">{`{{${varTooltip.name}}}`}</span>
                <span className="variable-tooltip-value">{varTooltip.value}</span>
              </div>
              <div className="variable-tooltip-meta">
                <span className="variable-tooltip-scope-badge">{varTooltip.type}</span>
                {!isDynamicVariable(varTooltip.name) && (
                  <span className="variable-tooltip-hint">· click to edit</span>
                )}
              </div>
            </>
          ) : (
            <div className="variable-tooltip-top">
              <span className="variable-tooltip-name">{`{{${varTooltip.name}}}`}</span>
              <span className="variable-tooltip-missing">Undefined</span>
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Monaco variable editor */}
      {editingVariable && (
        <VariableEditorPopup
          editingVariable={editingVariable}
          variableContext={variableContext}
          selectedEnvironment={selectedEnvironment}
          workspaceVariables={workspaceVariables}
          globalVariables={globalVariables}
          collectionVariables={collectionVariables}
          folderVariables={folderVariables}
          onVariableUpdate={onVariableUpdate}
          onSave={saveVariableValue}
          onClose={() => setEditingVariable(null)}
        />
      )}
    </div>
  );
}
